import apiClient from './client';

export interface ReviewRun {
  id: string;
  case_id: string;
  run_number: number;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  triggered_by: string;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  data_fetched_at: string | null;
}

export interface TriggeredFlag {
  id: string;
  flag_definition_id: string;
  flag_code: string;
  category: number;
  description: string;
  severity: 'AUTO_ESCALATE' | 'HIGH' | 'MEDIUM' | 'LOW';
  points: number;
  fact_summary: string;
  source_citation: string;
  provenance: Record<string, unknown>;
  is_cross_check: boolean;
  override_status?: 'overridden' | null;
  override_reason?: string | null;
  // learned-rule badge fields (populated by the review-detail API).
  learned_rule_version_id?: string | null;
  source_badge?: 'hardcoded' | 'learned';
  rule_name?: string | null;
  version_no?: number | null;
  confidence?: number | null;
}

export interface ReviewScore {
  aggregate_score: number;
  tier: 'STANDARD' | 'ENHANCED_REVIEW' | 'ESCALATE';
  has_auto_escalate: boolean;
}

export interface ReviewSummary {
  id: string;
  review_run_id: string;
  summary_text: string;
  not_verifiable_items: string[];
  worksheet_fields: Record<string, unknown>;
  generated_at: string;
}

export interface ReviewRunDetail {
  run: ReviewRun;
  score: ReviewScore | null;
  flags: TriggeredFlag[];
  summary: ReviewSummary | null;
}

export interface Determination {
  id: string;
  case_id: string;
  review_run_id: string;
  determined_by: string;
  decision: 'APPROVE' | 'ESCALATE' | 'REJECT';
  rationale: string;
  determined_at: string;
}

export interface PublicDataResult {
  id: string;
  source: string;
  query_type: string;
  query_value: string;
  result_data: Record<string, unknown>;
  source_url: string | null;
  screenshot_path: string | null;
  pdf_path: string | null;
  has_screenshot: boolean;
  has_pdf: boolean;
  retrieved_at: string;
  review_run_id: string | null;
}

export interface AuditEntry {
  id: string;
  user_id: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  timestamp: string;
}

export async function listReviewRuns(caseId: string): Promise<{ items: ReviewRun[] }> {
  const { data } = await apiClient.get(`/cases/${caseId}/reviews`);
  return data;
}

export async function startReview(caseId: string): Promise<ReviewRun> {
  const { data } = await apiClient.post<ReviewRun>(`/cases/${caseId}/reviews`);
  return data;
}

/** Trigger a full review in the background (POST /cases/{id}/review).
 * Returns immediately with HTTP 202 + {run_id, status:'RUNNING', case_id}.
 * Poll getReviewRun() until run.status is COMPLETED or FAILED. */
export async function runFullReview(
  caseId: string
): Promise<{ run_id: string; status: string; case_id: string }> {
  const { data } = await apiClient.post<{ run_id: string; status: string; case_id: string }>(
    `/cases/${caseId}/review`
  );
  return data;
}

export async function getReviewRun(caseId: string, runId: string): Promise<ReviewRunDetail> {
  const { data } = await apiClient.get<ReviewRunDetail>(`/cases/${caseId}/reviews/${runId}`);
  return data;
}

/** Mark a triggered flag as a false positive (override/dismiss) with a reason.
 * PATCH /flags/{flagId}/override. The reason is required + auditable.
 * Returns the updated flag (override_status='overridden'). */
export async function overrideFlag(flagId: string, reason: string): Promise<TriggeredFlag> {
  const { data } = await apiClient.patch<TriggeredFlag>(`/flags/${flagId}/override`, { reason });
  return data;
}

/** Cancel a runaway/stuck review run (user self-serve). POST /cases/{id}/reviews/{runId}/cancel.
 * Returns the updated ReviewRun (status='CANCELLED'). 409 if the run is no
 * longer RUNNING (already completed/failed/cancelled). */
export async function cancelReview(caseId: string, runId: string): Promise<ReviewRun> {
  const { data } = await apiClient.post<ReviewRun>(`/cases/${caseId}/reviews/${runId}/cancel`);
  return data;
}

export async function getDetermination(caseId: string): Promise<Determination | null> {
  try {
    const { data } = await apiClient.get<Determination>(`/cases/${caseId}/determination`);
    return data;
  } catch {
    return null;
  }
}

export async function recordDetermination(
  caseId: string,
  decision: Determination['decision'],
  rationale: string
): Promise<Determination> {
  const { data } = await apiClient.post<Determination>(`/cases/${caseId}/determination`, {
    decision,
    rationale,
  });
  return data;
}

export async function listPublicData(caseId: string, runId?: string): Promise<{ items: PublicDataResult[] }> {
  const params = runId ? { run_id: runId } : undefined;
  const { data } = await apiClient.get(`/cases/${caseId}/public-data`, { params });
  return data;
}

export async function getPublicDataHistory(caseId: string): Promise<{ case_id: string; history: Array<{ timestamp: string; source_count: number; sources: string[]; result_count: number; items: PublicDataResult[] }>; total_runs: number }> {
  const { data } = await apiClient.get('/cases/' + caseId + '/public-data/history');
  return data;
}

/** Enqueue a public-data refresh in the background (POST /cases/{id}/public-data).
 * Returns immediately with HTTP 202 + {run_id, status:'RUNNING', case_id, query_value}.
 * Poll getPublicDataRun() until run.status is COMPLETED or FAILED, then read
 * the cached results via listPublicData(). */
export async function triggerPublicLookup(
  caseId: string,
  queryType: string,
  queryValue: string,
  sources?: string[]
): Promise<{ run_id: string; status: string; case_id: string; query_value: string }> {
  const { data } = await apiClient.post(`/cases/${caseId}/public-data`, {
    query_type: queryType,
    query_value: queryValue,
    sources,
  });
  return data;
}

export interface PublicDataRun {
  run_id: string | null;
  case_id: string;
  status: string;
  query_value: string | null;
  sources: string[] | null;
  results_count: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

export async function getPublicDataRun(caseId: string, runId: string): Promise<PublicDataRun> {
  const { data } = await apiClient.get<PublicDataRun>(`/cases/${caseId}/public-data/runs/${runId}`);
  return data;
}

export async function getLatestPublicDataRun(caseId: string): Promise<PublicDataRun> {
  const { data } = await apiClient.get<PublicDataRun>(`/cases/${caseId}/public-data/runs/latest`);
  return data;
}

export async function listAuditEntries(
  caseId: string
): Promise<{ items: AuditEntry[]; total: number; page: number; page_size: number }> {
  try {
    const { data } = await apiClient.get('/audit/case/' + caseId);
    // Map entries to items for frontend consistency
    return { items: data.entries || [], total: data.total || 0, page: 1, page_size: 50 };
  } catch {
    return { items: [], total: 0, page: 1, page_size: 50 };
  }
}

export function downloadDocumentUrl(_caseId: string, docId: string): string {
  // Path relative to the apiClient baseURL (apiClient prepends /api/v1).
  return `/documents/${docId}/download`;
}

export function downloadStrippedDocumentUrl(_caseId: string, docId: string): string {
  // Data-washed (PII-stripped) copy produced at ingest (DIR-043).
  return `/documents/${docId}/download-stripped`;
}